import '../../register-web-components';
import { ReactElement, useEffect, useState } from 'react';
import Header from '../../components/header';
import Footer from '../footer';
import Head from 'next/head';
import { LOOKUP_PAGE_DESCRIPTION, LOOKUP_PAGE_TITLE } from '../../lib/constants';
import ConnectAnotherWayCard, {
  getDemographicLink,
  getIdentityInfoLink,
  getPersonalInfoLink,
  getPrescriptionLabelLink
} from '../../components/connectAnotherWayCard/connectAnotherWayCard';
import styles from './appLayout.module.scss';
import { useRouter } from 'next/router';
import {
  CAREMARK_LOB,
  findLob,
  getAdobeFlowName,
  getCurrentEnv,
  getFlowProps,
  getLobData,
  loadScript,
  SPECIALTY_LOB
} from '../../lib/utils/utils';
import { pageLoadAnalytics } from '../../lib/utils/analytics';
import UserContext from '../../context/UserContext';
import Script from 'next/script';
import Spinner from '../spinner';
import { Link, Tile } from '@digital-pulse/react';
import LobHeader from '../lobHeader/lobHeader';
import MemberIDModal from '../memberIDModal/memberIDModal';
import { CREATE_ACCOUNT_ROUTE } from '@/lib/constants/routes';
import LobFooter from '../lobFooter/lobFooter';
import {
  useConnectAnotherWayTranslations,
  useIdentityInfoTranslations
} from '../../lib/utils/translationHelpers';

type Props = {
  pageTitle: string;
  pageType?: string;
  pageTag?: string;
  showConnectCard?: boolean;
  showContactUs?: boolean;
  slimHeader?: boolean;
  children: ReactElement;
  helpCenter?: boolean;
  memberIDModal?: boolean;
  pageName?: string;
  action?: string;
  crossLOBUI?: boolean;
  showFooter?: boolean;
  onlyDOBFieldFlag?: boolean;
};

const isGitPreview = process?.env?.NEXT_PUBLIC_APP_ENV === 'preview';

const AppLayout = ({
  pageTitle,
  pageType = '',
  pageTag,
  showConnectCard = false,
  showContactUs = false,
  slimHeader = false,
  children,
  helpCenter = false,
  memberIDModal = false,
  pageName,
  action = CREATE_ACCOUNT_ROUTE,
  crossLOBUI = false,
  showFooter = false,
  onlyDOBFieldFlag = false
}: Props) => {
  const lob = findLob();
  const isUnifiedRegistration = [CAREMARK_LOB, SPECIALTY_LOB].includes(lob);
  const { CVS_TITLE, FAQ_URL } = getLobData();
  const env = getCurrentEnv();
  const title = (pageTitle || LOOKUP_PAGE_TITLE) + ' | ' + CVS_TITLE;
  const [isHeaderReady, setisHeaderReady] = useState(
    env === 'LOCAL' || isGitPreview ? true : false
  );
  const [isTagFired, setTagFired] = useState(false);
  const router = useRouter();
  const flow = router?.query?.flow;
  const [appProps, setAppProps] = useState(undefined);
  const [isThirdParty, setIsThirdParty] = useState(false);
  const { updateLookupData } = UserContext();
  const [memberIDModalOpen, setMemberIDModalOpen] = useState(false);
  const identityTranslations = useIdentityInfoTranslations();
  const connectTranslations = useConnectAnotherWayTranslations();

  useEffect(() => {
    if (!router?.isReady) return;
    const { returnUrl, flow, source } = router.query;
    const lookupFlow: any = flow || source || (isUnifiedRegistration && 'classic');
    if (lookupFlow) {
      const appProps = getFlowProps(lookupFlow);
      setAppProps(appProps);
      setIsThirdParty(appProps?.isThirdParty);
      updateLookupData({ flow: lookupFlow, appProps: appProps });

      if (lookupFlow && lookupFlow != '') {
        sessionStorage.setItem('lookupFlow', lookupFlow?.toString());
      }
      // handle page tagging
      if (lookupFlow && pageTag && !isTagFired) {
        pageLoadAnalytics(getAdobeFlowName(lookupFlow), pageTag);
        setTagFired(true);
      }
    }
    setisHeaderReady(true);
    if (returnUrl) {
      sessionStorage.setItem('postReturnUrl', returnUrl?.toString());
    }
  }, [router?.query]);

  let cardLinks;
  switch (pageType) {
    case 'rxlabel': {
      cardLinks = [getPersonalInfoLink(connectTranslations)];
      break;
    }
    case 'demographic': {
      cardLinks = [
        getPrescriptionLabelLink(connectTranslations),
        getPersonalInfoLink(connectTranslations)
      ];
      break;
    }
    case 'personal-info': {
      cardLinks = [getPrescriptionLabelLink(connectTranslations)];
      break;
    }
    case 'identity-rx': {
      cardLinks = [getIdentityInfoLink(connectTranslations)];
      break;
    }
    case 'identity-info': {
      cardLinks = [getPrescriptionLabelLink(connectTranslations)];
      break;
    }
    default: {
      cardLinks = [
        getDemographicLink(connectTranslations),
        getPersonalInfoLink(connectTranslations),
        getPrescriptionLabelLink(connectTranslations)
      ];
      break;
    }
  }
  // Use identity translations for help section if on identity-info page
  const helpText = identityTranslations?.HELP;
  const helpCenterText = identityTranslations?.HELP_CENTER;

  return (
    <>
      <Script
        id={'appdScript'}
        type={'text/javascript'}
        src={'https://www.cvs.com/static/scripts/appd.js'}
        data-appname={'/account/CVS-Lookup'}
        data-pagename={pageName || 'CVS-Lookup'}
        data-title={'CVS-Lookup'}
        onLoad={() => {
          loadScript('https://cdn.appdynamics.com/adrum/adrum-latest.js', 'adrumLatest');
        }}
      />
      <Head>
        <title>{title}</title>
        <meta name="description" content={LOOKUP_PAGE_DESCRIPTION} />
      </Head>
      {isUnifiedRegistration || crossLOBUI ? (
        <div className={styles.guest__container}>
          {isUnifiedRegistration ? (
            <LobHeader />
          ) : (
            <Header
              targetComponent={`main,${styles.main},false`}
              serviceProps={`slim_header=true`}
            />
          )}
          <main className={styles.guest__main} id="content" tabIndex={-1}>
            <Tile className={styles.card}>
              {children}
              {showContactUs && helpCenter && (
                <div className={styles.contactUs__container}>
                  <hr className={styles.hr} />
                  <h2 className={`${styles.help__header} pc-heading-m`}>{helpText}</h2>
                  <Link href={FAQ_URL} variant="standalone" weight="strong">
                    {helpCenterText}
                  </Link>
                  {memberIDModal && (
                    <MemberIDModal
                      memberIDModalOpen={memberIDModalOpen}
                      setMemberIDModalOpen={setMemberIDModalOpen}
                      translations={identityTranslations}
                    />
                  )}
                </div>
              )}
            </Tile>
          </main>
          {!isUnifiedRegistration && showFooter ? <Footer /> : <LobFooter lob={findLob()} />}
        </div>
      ) : (
        <>
          {!isThirdParty && (
            <Header targetComponent={`main,${styles.main},false`} slimHeader={slimHeader} />
          )}
          {onlyDOBFieldFlag ? (
            <Tile className={styles.dobCard}>
              <main className={`${styles.main} ${styles.dobMain} `} id="content" tabIndex={-1}>
                {!isHeaderReady && <Spinner />}
                {children}
              </main>
            </Tile>
          ) : (
            <main className={styles.main} id="content" tabIndex={-1}>
              {!isHeaderReady && <Spinner />}
              {children}
              {showConnectCard && appProps?.showCardLink && (
                <div className="connect_another_way_container">
                  <ConnectAnotherWayCard linkProps={cardLinks} flow={flow} />
                </div>
              )}
            </main>
          )}
          {!isThirdParty && <Footer />}
        </>
      )}
      <form method={'POST'} id={'registrationForm'} hidden={true} action={action}>
        <input name={'registerAccount'} />
      </form>
    </>
  );
};

export default AppLayout;
